import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LEGAL_ENABLED, LEGAL_URLS } from '../legal'
import type { PlanId } from '../plans'
import { checkCode, isConfirmed, openWithConfirmation, resendConfirmation } from '../services/activation'
import { useAuth } from '../context/AuthContext'
import { tenantIdCandidates } from '../services/credentials'
import { formatPhone, phoneE164, phoneProblem } from '../utils/phone'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  LockIcon,
  MailCheckIcon,
  MailIcon,
  PhoneIcon,
  RotateCwIcon,
  StoreIcon,
  UserIcon,
} from '../components/icons'
import './LoginPage.css'

/**
 * Opening an institution with an activation code.
 *
 * The code is checked on its own first, so nobody fills a form in only to be
 * told at the end that the code was never good. Then two short steps, the
 * business, then the person who runs it, and a last screen that waits for
 * the confirmation mail.
 *
 * The institution code is not asked for. It is made from the business name
 * and the server takes the first free one: the owner never needed to choose
 * it, only to type it, and a field asking for "küçük harf, rakam ve tire" was
 * the one place this form spoke the system's language instead of theirs.
 *
 * The account opens unconfirmed. The mail's "Onayla ve giriş yap" proves the
 * address and signs the owner in with the same press, landing them in their
 * own till.
 *
 * Nothing here is trusted. The server checks everything again, this
 * screen's validation is only so the person hears about a problem while they
 * are still looking at the field that caused it.
 */
type Stage = 'form' | 'sent'

/**
 * @param trial  "Ücretsiz başla": no code step, the institution opens as a
 *   14-day trial. The code route stays for customers set up by hand.
 */
export function ActivatePage({ trial = false }: { trial?: boolean }) {
  const navigate = useNavigate()

  const [code, setCode] = useState('')
  const [verified, setVerified] = useState(trial)
  const [stage, setStage] = useState<Stage>(() =>
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('onizleme') === 'posta'
      ? 'sent'
      : 'form',
  )

  const [displayName, setDisplayName] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [phone, setPhone] = useState('')
  /** Hata ancak alandan çıkınca: yazarken her tuşta "eksik" demek bağırmak olur. */
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  /** Her şifre kutusunun gözü yalnızca kendini açıyor (kullanıcı istedi). */
  const [pwShown, setPwShown] = useState(false)
  const [pw2Shown, setPw2Shown] = useState(false)

  /** Adımlar: işletme · iletişim · şifre ve onaylar. */
  const steps = ['biz', 'contact', 'pass'] as const
  const [stepIx, setStepIx] = useState(0)
  const step = steps[stepIx]
  const [termsOk, setTermsOk] = useState(false)
  const [privacyOk, setPrivacyOk] = useState(false)
  const [kvkkOk, setKvkkOk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** What the server opened, for the last screen and the resend button. */
  // Yalnızca geliştirmede: /kurulum?onizleme=posta bu ekranı kod harcamadan
  // açıyor. Yayınlanan sürümde import.meta.env.DEV false ve dal hiç yok.
  const preview =
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('onizleme') === 'posta'
  const [opened, setOpened] = useState<{ tenantId: string; email: string } | null>(
    preview ? { tenantId: 'onizleme', email: 'ornek@eposta.com' } : null,
  )
  const [resendNote, setResendNote] = useState<string | null>(null)
  const { switchTenant, login } = useAuth()
  /** Onay geldi, giriş yapılıyor. */
  const [entering, setEntering] = useState(false)

  // Onayı bekle: birkaç saniyede bir ve sekmeye geri dönülünce hemen sor.
  // Onaylanınca bu ekran, kaydı yapan kişi adına kendiliğinden giriş yapar;
  // postadaki bağlantı telefonda tarayıcıda açılsa bile uygulama burada açılır.
  useEffect(() => {
    if (stage !== 'sent' || !opened || preview || !password) return
    let alive = true
    let busyCheck = false
    const check = async () => {
      if (busyCheck || !alive) return
      busyCheck = true
      try {
        if (await isConfirmed(opened.email, opened.tenantId)) {
          if (!alive) return
          alive = false
          setEntering(true)
          switchTenant(opened.tenantId)
          await login(opened.email, password, opened.tenantId)
          navigate(`/${opened.tenantId}`, { replace: true })
        }
      } catch {
        // Giriş olmadıysa (şifre başka cihazda değiştirildiyse) normal giriş ekranı.
        if (!alive) navigate('/giris', { replace: true })
      } finally {
        busyCheck = false
      }
    }
    const t = window.setInterval(() => void check(), 4000)
    const onVisible = () => document.visibilityState === 'visible' && void check()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      alive = false
      window.clearInterval(t)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, opened, preview])

  const onCheck = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      // The panel lists codes as "KRM-3X76-••••", a label, not a code. It is
      // the one wrong thing that looks exactly like the right thing, so it is
      // worth naming rather than letting the generic message take the blame.
      if (code.includes('•')) {
        setError(
          'Bu, panelde görünen kısaltılmış etiket. Kodun tamamını, ' +
            'üretildiğinde kopyaladığınız haliyle girin.',
        )
        return
      }

      const ok = await checkCode(code)
      if (!ok) {
        setError('Kod geçersiz veya süresi dolmuş.')
        return
      }
      setVerified(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kod doğrulanamadı.')
    } finally {
      setBusy(false)
    }
  }

  const candidates = tenantIdCandidates(displayName)
  const nameOk = displayName.trim() !== '' && candidates.length > 0

  // Loose on purpose: the confirmation mail is what really checks the
  // address, and a stricter pattern would only turn away valid ones.
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  /**
   * Gözden kaçan en sık yanlış "@" unutmak (kullanıcı istedi). Alandan
   * çıkınca ya da @ yazılmadan adres uzayınca söyleniyor; yazarken her harfte
   * değil.
   */
  const [emailTouched, setEmailTouched] = useState(false)
  const emailText = email.trim()
  const emailError = !emailText
    ? null
    : !emailText.includes('@')
      ? 'E-posta adresinde @ işareti yok. Adresinizi kontrol edin.'
      : !emailOk
        ? 'E-posta adresi eksik görünüyor (örn. ad@gmail.com).'
        : null
  const showEmailError = emailError && (emailTouched || (!emailText.includes('@') && emailText.length > 12))

  const passwordProblem = password && password.length < 8
    ? 'Şifre en az 8 karakter olmalı.'
    : password2 && password !== password2
      ? 'Şifreler birbirini tutmuyor.'
      : null

  const phoneError = phoneProblem(phone)

  const canSubmit =
    !busy &&
    nameOk &&
    ownerName.trim() !== '' &&
    !phoneError &&
    emailOk &&
    password !== '' &&
    password === password2 &&
    !passwordProblem &&
    (!LEGAL_ENABLED || (termsOk && privacyOk && kvkkOk))

  /** Bu adımın alanları tamam mı: İleri ancak o zaman açılıyor. */
  const okFor = (s: (typeof steps)[number]) =>
    (s === 'biz' && nameOk && ownerName.trim() !== '') ||
    (s === 'contact' && emailOk && !phoneError) ||
    (s === 'pass' && canSubmit)
  const stepOk = okFor(step)
  /** Numaraya basınca: geriye her zaman, ileriye aradaki adımlar tamamsa. */
  const canJump = (k: number) => k <= stepIx || steps.slice(0, k).every(okFor)

  const onCreate = async (e: FormEvent) => {
    e.preventDefault()
    // Enter son adımdan önce bir sonraki adıma geçiriyor, kaydı göndermiyor.
    if (step !== 'pass') {
      if (step === 'contact') {
        setPhoneTouched(true)
        setEmailTouched(true)
      }
      if (stepOk) {
        setError(null)
        setStepIx((i) => i + 1)
      }
      return
    }
    if (!canSubmit) return
    setBusy(true)
    setError(null)

    const mail = email.trim().toLowerCase()
    try {
      const result = await openWithConfirmation({
        code,
        tenantIds: candidates,
        displayName: displayName.trim(),
        ownerName: ownerName.trim(),
        phone: phoneE164(phone),
        email: mail,
        password,
        plan: trial ? ('max' satisfies PlanId) : undefined,
      })
      setOpened({ tenantId: result.tenantId, email: mail })
      if (!result.mailSent) {
        setResendNote('Posta gönderilemedi. Aşağıdan tekrar gönderin.')
      }
      setStage('sent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kurum açılamadı.')
    } finally {
      setBusy(false)
    }
  }

  const onResend = async () => {
    if (!opened) return
    setBusy(true)
    setResendNote(null)
    try {
      await resendConfirmation(opened.email, opened.tenantId)
      setResendNote('Posta yeniden gönderildi.')
    } catch (err) {
      setResendNote(err instanceof Error ? err.message : 'Posta gönderilemedi.')
    } finally {
      setBusy(false)
    }
  }

  if (!verified && !preview) {
    return (
      <div className="login login--solo">
        <form className="login__card wiz" onSubmit={onCheck} autoComplete="off">
          <div className="wiz__head">
            <div className="wiz__icon">
              <KeyRoundIcon />
            </div>
            <h1>Kurulum</h1>
            <p className="login__lead">
              Size verilen aktivasyon koduyla işletmenizi açın.
            </p>
          </div>

          <div className="fld">
            <label htmlFor="code">Aktivasyon kodu</label>
            <input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onClick={(e) => e.currentTarget.select()}
              placeholder="RST-XXXX-XXXX"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>

          {error && (
            <div className="login__err" role="alert">
              {error}
            </div>
          )}

          <div className="wiz__actions">
            <button type="submit" className="btn btn--primary btn--lg" disabled={busy || !code}>
              {busy ? 'Kontrol ediliyor…' : 'Devam'}
              {!busy && <ArrowRightIcon />}
            </button>
          </div>

          <button
            type="button"
            className="login__change-tenant"
            // Bare "/giris" when this screen was reached without a kurum in
            // the address, that sign-in works out the institution itself.
            onClick={() => navigate('/giris', { replace: true })}
          >
            <ArrowLeftIcon />
            Girişe dön
          </button>
        </form>
      </div>
    )
  }

  if ((stage === 'sent' || preview) && opened) {
    return (
      <div className="login login--solo">
        <div className="login__card wiz wiz--sent">
          {/* Ne beklendiğini sözden önce söylüyor: ekranın konusu bir posta. */}
          <div className="wiz__icon">
            <MailCheckIcon />
          </div>
          <h1>E-postanızı onaylayın</h1>
          <p className="login__lead">
            <b>{opened.email}</b> adresine bir posta gönderdik. İçindeki{' '}
            <b>Onayla ve giriş yap</b> düğmesine bastığınızda panelinize erişebileceksiniz.
          </p>
          <p className="fld__hint">
            Birkaç dakika içinde gelmezse gereksiz (spam) klasörüne bakın.
          </p>

          {/* Onay beklenirken: kişi bu ekrana döndüğünde kendiliğinden içeri girer. */}
          <p className="wiz__waiting" role="status">
            <span className="wiz__dot" aria-hidden="true" />
            {entering
              ? 'E-postanız onaylandı, panelinize giriliyor…'
              : 'Onayınızı bekliyoruz. Postadaki bağlantıya bastığınızda bu ekran kendiliğinden açılır.'}
          </p>

          {resendNote && <p className="wiz__note">{resendNote}</p>}

          <div className="wiz__actions">
            <button
              type="button"
              className="btn btn--outline btn--lg"
              onClick={() => void onResend()}
              disabled={busy}
            >
              <RotateCwIcon />
              {busy ? 'Gönderiliyor…' : 'Postayı tekrar gönder'}
            </button>
          </div>

          <button
            type="button"
            className="login__change-tenant"
            onClick={() => navigate('/giris', { replace: true })}
          >
            <ArrowLeftIcon />
            Girişe dön
          </button>
        </div>
      </div>
    )
  }

  const field = (
    id: string,
    label: string,
    icon: JSX.Element,
    input: JSX.Element,
    error?: string | null,
  ) => (
    <div className="fld">
      <label htmlFor={id}>{label}</label>
      <div className="fld__with-icon">
        <span className="fld__icon" aria-hidden="true">{icon}</span>
        {input}
      </div>
      {error && <p className="fld__hint fld__hint--bad">{error}</p>}
    </div>
  )

  // Tek sayfa: işletme ve yetkili aynı formda. Açıklama yok, alan adı ve
  // ikon ne istendiğini söylüyor; yalnızca bir yanlış olunca kırmızı satır.
  return (
    <div className="login login--solo">
      <form className="login__card wiz" onSubmit={onCreate} autoComplete="off">
        {/* Simge yok: adım göstergesi zaten ne yapıldığını söylüyor ve
            bilgisayarda kartı ekrandan taşırıyordu. */}
        <div className="wiz__head">
          <h1>{trial ? 'Ücretsiz deneyin' : 'Kurum bilgileri'}</h1>
          {trial && (
            <p className="wiz__trial">14 gün ücretsiz, kredi kartı gerekmez</p>
          )}
        </div>

        {/* Adımlar: sayı dairesi ve aralarında çizgi; geçilenler dolu. */}
        <ol className="wiz__steps" aria-label="Kayıt adımları">
          {steps.map((s, k) => (
            <li key={s} className={k < stepIx ? 'is-done' : k === stepIx ? 'is-on' : ''}>
              <button
                type="button"
                disabled={k === stepIx || !canJump(k)}
                onClick={() => {
                  setError(null)
                  setStepIx(k)
                }}
                aria-label={`${k + 1}. adım`}
                aria-current={k === stepIx ? 'step' : undefined}
              >
                {k < stepIx ? <CheckIcon /> : k + 1}
              </button>
            </li>
          ))}
        </ol>

        {step === 'biz' && (
          <>
        {field('dname', 'İşletme adı', <StoreIcon />, (
          <input
            id="dname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Örn. Mercan Restoran"
          />
        ))}
        {field('oname', 'Ad soyad', <UserIcon />, (
          <input
            id="oname"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Örn. Ahmet Mercan"
            autoComplete="name"
          />
        ))}
          </>
        )}

        {step === 'contact' && (
          <>
        {field('mail', 'E-posta', <MailIcon />, (
          <input
            id="mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            placeholder="ornek@eposta.com"
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="email"
          />
        ), showEmailError ? emailError : null)}
        {field('tel', 'Telefon', <PhoneIcon />, (
          <input
            id="tel"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            onBlur={() => setPhoneTouched(true)}
            placeholder="(5xx) xxx xx xx"
          />
        ), phoneTouched ? phoneError : null)}
          </>
        )}

        {step === 'pass' && (
          <>
          <div className="fld">
            <label htmlFor="pw">Şifre</label>
            <div className="fld__with-toggle fld__with-icon">
              <span className="fld__icon" aria-hidden="true"><LockIcon /></span>
              <input
                id="pw"
                type={pwShown ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="fld__toggle"
                onClick={() => setPwShown((v) => !v)}
                tabIndex={-1}
                aria-label={pwShown ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {pwShown ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          <div className="fld">
            <label htmlFor="pw2">Şifre (tekrar)</label>
            <div className="fld__with-toggle fld__with-icon">
              <span className="fld__icon" aria-hidden="true"><LockIcon /></span>
              <input
                id="pw2"
                type={pw2Shown ? 'text' : 'password'}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="fld__toggle"
                onClick={() => setPw2Shown((v) => !v)}
                tabIndex={-1}
                aria-label={pw2Shown ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {pw2Shown ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

        {/* Yasal onaylar. İkisi ayrı: sözleşme kabul edilir, aydınlatma metni
            ise okunduğu onaylanır (KVKK onu "rıza" olarak değil bilgi olarak
            ister). Bağlantılar yeni sekmede: yarım form kaybolmasın. */}
            {/* Yasal onaylar, her metin ayrı kutu. Aydınlatma metni "okudum":
                KVKK onu rıza değil bilgilendirme olarak istiyor. Bağlantılar
                yeni sekmede: yarım form kaybolmasın. */}
            {LEGAL_ENABLED && (
            <div className="login__consents">
              <label className="login__remember login__consent">
                <input type="checkbox" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} />
                <span>
                  <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer">Kullanıcı Sözleşmesi</a>’ni
                  okudum, kabul ediyorum.
                </span>
              </label>
              <label className="login__remember login__consent">
                <input type="checkbox" checked={privacyOk} onChange={(e) => setPrivacyOk(e.target.checked)} />
                <span>
                  <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer">Gizlilik Politikası</a>’nı
                  okudum, kabul ediyorum.
                </span>
              </label>
              <label className="login__remember login__consent">
                <input type="checkbox" checked={kvkkOk} onChange={(e) => setKvkkOk(e.target.checked)} />
                <span>
                  <a href={LEGAL_URLS.kvkk} target="_blank" rel="noreferrer">KVKK Aydınlatma Metni</a>’ni
                  okudum.
                </span>
              </label>
            </div>
            )}
          </>
        )}

        {(passwordProblem || error) && (
          <div className="login__err" role="alert">
            {passwordProblem ?? error}
          </div>
        )}

        <div className={`wiz__actions ${stepIx > 0 ? 'wiz__actions--pair' : ''}`}>
          {stepIx > 0 && (
            <button
              type="button"
              className="btn btn--outline btn--lg"
              onClick={() => {
                setError(null)
                setStepIx((i) => i - 1)
              }}
            >
              <ArrowLeftIcon />
              Geri
            </button>
          )}
          <button type="submit" className="btn btn--primary btn--lg" disabled={!stepOk || busy}>
            {step !== 'pass' ? 'İleri' : busy ? 'Kuruluyor…' : trial ? 'Kayıt ol' : 'Kurulumu tamamla'}
            {!busy && <ArrowRightIcon />}
          </button>
        </div>

        <button
          type="button"
          className="login__change-tenant"
          onClick={() => navigate('/giris', { replace: true })}
        >
          <ArrowLeftIcon />
          Girişe dön
        </button>
      </form>
    </div>
  )
}
